import React, { useState, useEffect } from "react";
import { useTranslation } from "@lib/i18n";
import { bool } from "prop-types";
import { produce } from "immer";

import { usePreconfigurationsSelector } from "@redux/reducers/appConfig";
import Typography from "@components/shared/Typography";
import PreconfigurationItem from "./PreconfigurationItem";
import SimpleButton from "@components/shared/SimpleButton";

const PreconfigurationList = ({ isOpen }) => {
  const { t } = useTranslation();

  const [selectedSubgroup, setSelectedSubgroup] = useState(null);

  const preconfigurations = usePreconfigurationsSelector();

  const targetGroups = preconfigurations.reduce((acc, cur) => {
    if (acc.length === 0) {
      return [[[cur]]];
    }

    const lastGroup = acc[acc.length - 1];
    const lastSubgroup = lastGroup[lastGroup.length - 1];
    const lastItem = lastSubgroup[lastSubgroup.length - 1];

    const { groupLabel, subgroupLabel } = lastItem;

    if (cur.groupLabel !== groupLabel) {
      return produce(acc, draft => {
        draft.push([[cur]]);
      });
    }

    if (cur.subgroupLabel !== subgroupLabel) {
      return produce(acc, draft => {
        draft[acc.length - 1].push([cur]);
      });
    }

    return produce(acc, draft => {
      draft[acc.length - 1][lastGroup.length - 1].push(cur);
    });
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSelectedSubgroup(null);
    }
  }, [isOpen]);

  return (
    <div
      className="flex flex-col items-start px-4"
      data-id="car-PreconfigurationPicker-container"
    >
      {selectedSubgroup ? (
        <div className="flex flex-col items-start gap-2 w-full py-4 last:pb-2 last:border-b last:border-b-white">
          <SimpleButton
            className="text-white m-0 mb-4 p-0 py-1"
            onClick={() => setSelectedSubgroup(null)}
            withArrow
          >
            {t("preconfiguration.back")}
          </SimpleButton>
          {selectedSubgroup.map((preconfiguration, index) => (
            <PreconfigurationItem
              key={index}
              index={index}
              setSelectedSubgroup={setSelectedSubgroup}
              singlePreconfiguration={preconfiguration}
              label={preconfiguration.label}
            />
          ))}
        </div>
      ) : (
        <>
          {targetGroups.map((preconfigurationGroups, index) => (
            <div
              className="w-full border-b border-b-greyLight pb-2 mb-2"
              key={index}
            >
              <Typography className="flex justify-start w-full my-3 text-sm text-greyLight font-light whitespace-pre-wrap">
                {preconfigurationGroups[0][0].groupLabel}
              </Typography>
              {preconfigurationGroups.map((subgroup, index) => (
                <div key={index}>
                  {subgroup.length > 1 ? (
                    <PreconfigurationItem
                      index={index}
                      setSelectedSubgroup={setSelectedSubgroup}
                      label={subgroup[0].subgroupLabel}
                      preconfigurationSubGroup={subgroup}
                    />
                  ) : (
                    <PreconfigurationItem
                      setSelectedSubgroup={setSelectedSubgroup}
                      singlePreconfiguration={subgroup[0]}
                      label={subgroup[0].label}
                    />
                  )}
                </div>
              ))}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

PreconfigurationList.propTypes = { isOpen: bool.isRequired };

export default PreconfigurationList;
